import { useCallback } from 'react';

import { PRODUCT_BASE_PATHS } from '@cast/console-constants';
import { useNavigate } from '@cast/infrastructure-organization';
import { useCluster } from '@cast/shared-cluster-context-data-access';
import { useOrganizations } from '@cast/shared-organization-context-data-access';
import {
  ExploreAutomationBanner,
  useExploreAutomationBannerDismiss,
  useShouldShowAutomationUpsell,
  type ExploreAutomationBannerVariant,
} from '@cast/shared-trial-ui';

type Props = {
  variant: ExploreAutomationBannerVariant;
};

const getAutomationOverviewPath = (clusterId: string): string =>
  `${PRODUCT_BASE_PATHS.automation}/external-clusters/${clusterId}/settings`;

/**
 * Paid reporting + PLG pre-trial → Explore automation CTA.
 * Dashboard variant is dismissible (org-scoped localStorage).
 */
export const ExploreAutomationUpsellBanner = ({ variant }: Props) => {
  const showUpsell = useShouldShowAutomationUpsell();
  const { cluster } = useCluster();
  const { currentOrganization } = useOrganizations();
  const navigate = useNavigate();
  const closable = variant === 'dashboard';
  const { isDismissed, dismiss } = useExploreAutomationBannerDismiss(
    currentOrganization?.id
  );

  const onExplore = useCallback(() => {
    navigate(getAutomationOverviewPath(cluster.id));
  }, [cluster.id, navigate]);

  if (!showUpsell) {
    return null;
  }
  if (closable && isDismissed) {
    return null;
  }

  return (
    <ExploreAutomationBanner
      variant={variant}
      onExplore={onExplore}
      closable={closable}
      onClose={closable ? dismiss : undefined}
    />
  );
};
